/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Controller } from 'react-hook-form';
import { Skeleton } from '@mui/material';
import {
  FormDialog,
  FormDialogActions,
  FormDialogContent,
  FormDialogTitle,
  FormElement,
  FormInputsStack,
  FormSubmissionError,
  FormCancelButton,
  Inputs,
  InputPhone,
  InputSkeleton,
  getSubmitButtonProps,
} from '@marageti/z4-lib';
import { DatePicker } from '@mui/x-date-pickers';
import { LoadingButton } from '@mui/lab';
import { ViewProps } from './types';
import { validateEmail, validatePhone, validateUrl } from '../../../../../utils/helpers/validation';

const EditUserFormView = ({
  control,
  errors,
  handleSubmit,
  isSubmitting,
  open,
  onCancel,
  submitHandler,
  register,
  submissionError,
  partner,
}: ViewProps) => (
  <FormDialog
    open={open}
  >
    <FormDialogTitle handleClose={onCancel}>
      {partner?.name
        ? 'Edit Info'
        : <Skeleton variant="rounded" />}
    </FormDialogTitle>
    <FormElement name="partner-editor-form" onSubmit={handleSubmit(submitHandler)}>
      <FormDialogContent>
        {!partner ? (
          <FormInputsStack>
            <InputSkeleton key="name" />
            <InputSkeleton key="onboardedOn" />
            <InputSkeleton key="yearFounded" />
            <InputSkeleton key="email" />
            <InputSkeleton key="phone" />
            <InputSkeleton key="sms-phone" />
            <InputSkeleton key="prl" />
            <InputSkeleton key="website" />
            <InputSkeleton key="public-url" />
            <InputSkeleton key="hubspot-url" />
          </FormInputsStack>
        ) : (
          <FormInputsStack>
            {submissionError && (
              <FormSubmissionError
                {...submissionError}
                message={
                  submissionError.message
                    ? submissionError.message.charAt(0).toUpperCase() + submissionError.message.slice(1)
                    : submissionError.message
                }
              />
            )}
            <Inputs.Text
              defaultValue={partner.name}
              error={!!errors?.name}
              helperText={errors?.name?.message as string}
              id="name"
              label="Name"
              required
              {...register('name', { required: 'Please enter a name' })}
            />
            <Controller
              name="onboardedOn"
              control={control}
              defaultValue={new Date(partner.onboardedOn)}
              render={({
                field: {
                  onChange, onBlur, ref, name, value,
                },
              }) => (
                <DatePicker
                  label="Onboarded"
                  onChange={onChange}
                  minDate={new Date(2007, 0, 1)}
                  maxDate={new Date()}
                  slotProps={{
                    textField: { name, onBlur, error: !!errors.onboardedOn },
                  }}
                  value={value}
                  inputRef={ref}
                />
              )}
            />
            <Inputs.Number
              defaultValue={partner.yearFounded}
              error={!!errors?.yearFounded}
              helperText={errors?.yearFounded ? 'Please enter a valid year' : ''}
              id="yearFounded"
              label="Founded"
              {...register('yearFounded', { valueAsNumber: true, min: 1000, max: new Date(Date.now()).getFullYear() })}
            />
            <Controller
              control={control}
              defaultValue={partner.email}
              name="email"
              render={({ field }) => (
                <Inputs.Text
                  error={!!errors?.email}
                  id="email"
                  helperText={errors?.email ? 'Please enter a valid email address' : ''}
                  label="Email"
                  {...field}
                />
              )}
              rules={{ validate: validateEmail }}
            />
            <Controller
              control={control}
              defaultValue={partner.usPhone}
              name="usPhone"
              render={({ field }) => (
                <InputPhone
                  error={!!errors.usPhone}
                  id="usPhone"
                  helperText={errors.usPhone ? 'Please enter a valid phone number' : ''}
                  label="US Phone"
                  {...field}
                />
              )}
              rules={{ validate: validatePhone }}
            />
            <Controller
              control={control}
              defaultValue={partner.primaryPhone}
              name="primaryPhone"
              render={({ field }) => (
                <InputPhone
                  error={!!errors.primaryPhone}
                  id="primaryPhone"
                  helperText={errors.primaryPhone ? 'Please enter a valid phone number' : ''}
                  label="Primary Phone"
                  {...field}
                />
              )}
              rules={{ validate: validatePhone }}
            />
            <Controller
              control={control}
              defaultValue={{ prefix: '', number: partner.smsPhoneNumber || '' }}
              name="smsPhone"
              render={({ field }) => (
                <InputPhone
                  error={!!errors.smsPhone}
                  id="smsPhone"
                  helperText={errors.smsPhone ? 'Please enter a valid phone number' : ''}
                  label="SMS Number"
                  phoneInputProps={{ countries: ['US'], countryCallingCodeEditable: false }}
                  {...field}
                />
              )}
              rules={{ validate: validatePhone }}
            />
            <Controller
              control={control}
              defaultValue={partner.website}
              name="website"
              render={({ field }) => (
                <Inputs.Text
                  error={!!errors?.website}
                  id="website"
                  helperText={errors?.website ? 'Please enter a valid url' : ''}
                  label="Website"
                  {...field}
                />
              )}
              rules={{ validate: validateUrl }}
            />
            <Controller
              control={control}
              defaultValue={partner.crmProfile}
              name="crmProfile"
              render={({ field }) => (
                <Inputs.Text
                  error={!!errors?.crmProfile}
                  id="crmProfile"
                  helperText={errors?.crmProfile ? 'Please enter a valid url' : ''}
                  label="Hubspot URL"
                  {...field}
                />
              )}
              rules={{ validate: validateUrl }}
            />
          </FormInputsStack>
        )}
      </FormDialogContent>
      <FormDialogActions>
        <FormCancelButton onClick={onCancel} />
        <LoadingButton
          variant="contained"
          type="submit"
          {...getSubmitButtonProps(isSubmitting, true)}
        >
          Submit
        </LoadingButton>
      </FormDialogActions>
    </FormElement>
  </FormDialog>
);

export default EditUserFormView;
